import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/firebase-client';
import { Loader2 } from 'lucide-react';
import { PageErrorBoundary } from './PageErrorBoundary';
import { useEmployerAccessState } from '@/hooks/useEmployerAccessState';
import { auth } from '@/lib/firebase';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'candidate' | 'employer';
}

export const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
  const { user, loading, getUserRole, activeRole } = useAuth();
  const [role, setRole] = useState<string | null>(activeRole);
  // If we already have activeRole from localStorage, don't block render on role fetch
  const [roleLoading, setRoleLoading] = useState(!activeRole);
  const location = useLocation();

  // Use cached profile_completed from localStorage to avoid blocking render on Firestore fetch.
  // Cache key is per-user so it doesn't leak between accounts.
  const cachedProfileCompleted = user?.uid
    ? localStorage.getItem(`intellr_profile_completed_${user.uid}`)
    : null;
  const [profileCompleted, setProfileCompleted] = useState<boolean | null>(
    // Employers never need this check; use cached value for candidates if available
    requiredRole === 'employer'
      ? true
      : cachedProfileCompleted !== null
        ? cachedProfileCompleted === 'true'
        : null
  );

  const { data: employerAccess, isLoading: employerAccessLoading } = useEmployerAccessState(requiredRole === 'employer' && role === 'employer');

  // Check if user has current auth state
  const hasCurrentUser = !!auth.currentUser;

  useEffect(() => {
    const fetchRole = async () => {
      // Use auth.currentUser as fallback when useAuth's user state hasn't caught up yet
      const effectiveUser = user || (hasCurrentUser ? { id: auth.currentUser!.uid, uid: auth.currentUser!.uid } : null);
      
      if (effectiveUser) {
        try {
          const userRole = await getUserRole();
          setRole(userRole);

          // Check onboarding completion for candidates
          if (userRole === 'candidate' && !location.pathname.startsWith('/candidate/onboarding')) {
            if (!effectiveUser.id && !effectiveUser.uid) {
              setProfileCompleted(false);
            } else {
              const userId = effectiveUser.id || effectiveUser.uid;
              const { data, error } = await supabase
                .from('profiles')
                .select('profile_completed')
                .eq('user_id', userId)
                .single();

              if (error) {
                console.warn('Profile check error:', error);
                setProfileCompleted(false);
              } else {
                const completed = data?.profile_completed ?? false;
                setProfileCompleted(completed);
                // Cache per-user so next refresh doesn't need Firestore
                if (userId) {
                  localStorage.setItem(`intellr_profile_completed_${userId}`, String(completed));
                }
              }
            }
          } else {
            setProfileCompleted(true); // Non-candidate or onboarding path - skip check
            // Cache employer completion so we skip this check on refresh too
            const uid = effectiveUser?.id || effectiveUser?.uid;
            if (uid && userRole === 'employer') {
              localStorage.setItem(`intellr_profile_completed_${uid}`, 'true');
            }
          }
        } catch (err) {
          console.error('Role fetch error:', err);
          // Fallback logic: Check localStorage first, then default to employer for gowithankit@gmail.com
          const cachedRole = localStorage.getItem('intellr_active_role');

          if (cachedRole && (cachedRole === 'employer' || cachedRole === 'candidate')) {
            setRole(cachedRole);
          } else {
            setRole('candidate'); // Default new users to candidate
          }
          setProfileCompleted(true);
        }
      } else {
        setProfileCompleted(true); // No user, skip check so it can redirect
      }
      setRoleLoading(false);
    };

    // Trigger fetchRole when loading is done
    if (!loading) {
      fetchRole();
    }
  }, [user, loading, getUserRole, location.pathname, hasCurrentUser]);

  // ✅ FIX: Don't redirect if handling OAuth callback (status/provider params present)
  // This prevents race condition where ProtectedRoute redirects before Profile.tsx handles callback
  const isOAuthCallback = location.search.includes('status=') && location.search.includes('provider=');

  // Check if we're about to redirect for incomplete onboarding (treat as loading state to prevent flicker)
  const needsOnboardingRedirect =
    requiredRole === 'candidate' &&
    profileCompleted === false && // explicitly false, not null
    !location.pathname.startsWith('/candidate/onboarding') &&
    !location.pathname.startsWith('/candidate/voice-onboarding') &&
    !isOAuthCallback;

  // Show loading state while auth and role are being determined OR while about to redirect
  if (loading || roleLoading || profileCompleted === null || needsOnboardingRedirect || (requiredRole === 'employer' && role === 'employer' && employerAccessLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  // Check authentication
  if (!user) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?returnUrl=${returnUrl}`} state={{ from: location }} replace />;
  }

  // Email verification enforcement (skip for OAuth users who are auto-verified)
  // Block access to all protected pages except /verify-email
  if (
    auth.currentUser &&
    auth.currentUser.email &&
    !auth.currentUser.emailVerified &&
    !location.pathname.startsWith('/verify-email')
  ) {
    return <Navigate to="/verify-email" replace />;
  }

  if (requiredRole === 'employer' && role === 'employer' && employerAccess) {
    // IMPORTANT: Check exact path, not startsWith, to avoid matching /employer/onboarding-tasks
    const onOnboardingPage = location.pathname === '/employer/onboarding';
    const onPendingPage = location.pathname.startsWith('/employer/access-pending');

    if (employerAccess.status === 'pending' && !onPendingPage) {
      return <Navigate to="/employer/access-pending" replace />;
    }

    if (employerAccess.status === 'needs_onboarding' && !onOnboardingPage) {
      return <Navigate to="/employer/onboarding" replace />;
    }

    if (employerAccess.status === 'active' && (onOnboardingPage || onPendingPage)) {
      return <Navigate to="/employer/dashboard" replace />;
    }
  }

  // Onboarding guard: redirect incomplete candidates to onboarding (preserve returnUrl)
  // Skip check if: already on onboarding, handling OAuth callback, or on voice onboarding
  if (
    requiredRole === 'candidate' &&
    !profileCompleted &&
    !location.pathname.startsWith('/candidate/onboarding') &&
    !location.pathname.startsWith('/candidate/voice-onboarding') &&
    !isOAuthCallback
  ) {
    const onboardingReturnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/candidate/onboarding?returnUrl=${onboardingReturnUrl}`} replace />;
  }

  // SMART REDIRECT: Auto-switch role if user has permission but it's not active
  if (requiredRole && role !== requiredRole && role !== 'superadmin' && role !== null) {
    // If we're here, it means the role doesn't match and it's not loading
    // instead of redirecting away, we check if they have the role and switch it
    // but we do this in a side effect or on the next pass
    return <AutoRoleSwitcher requiredRole={requiredRole} currentRole={role} />;
  }

  // Derive page name from the URL path for error reporting
  const pageName = location.pathname.split('/').filter(Boolean).pop() || 'Unknown';

  return <PageErrorBoundary pageName={pageName}>{children}</PageErrorBoundary>;
};

// Helper component to handle the side-effect of role switching without crashing render
const AutoRoleSwitcher = ({ requiredRole, currentRole }: { requiredRole: 'candidate' | 'employer'; currentRole: string }) => {
  const { getUserRoles, switchRole } = useAuth();
  const [isAllowed, setIsAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAndSwitch = async () => {
      const roles = await getUserRoles();
      if (roles.includes(requiredRole)) {
        switchRole(requiredRole);
        setIsAllowed(true);
      } else {
        setIsAllowed(false);
      }
    };
    checkAndSwitch();
  }, [requiredRole, getUserRoles, switchRole]);

  if (isAllowed === false) {
    // If definitely not allowed, redirect to their actual dashboard
    return <Navigate to={`/${currentRole}/dashboard`} replace />;
  }

  // While checking or switching, show loader
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-8 h-8 animate-spin text-accent" />
    </div>
  );
};

export default ProtectedRoute;
